import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { apiClient } from '../api/client'
import { useCan } from '../contexts/PermissionsContext'
import { Alert, AlertDescription } from './ui/alert'
import { Badge } from './ui/badge'

/**
 * The lease's primary object (spec 2026-10-25-06 §1): a radio per linked asset
 * for a writer, a badge for a reader. Its notice and its rent hang on it.
 */
export function PrimaryAssetRadio({ org, contractUid, assetUid, isPrimary }: { org: string; contractUid: string; assetUid: string; isPrimary: boolean }) {
  const { t } = useTranslation('contracts')
  const canWrite = useCan('contracts.write')
  const queryClient = useQueryClient()
  const [saving, setSaving] = useState(false)
  if (!canWrite) {
    return isPrimary ? <Badge variant="outline" data-testid={`contract-asset-primary-badge-${assetUid}`}>{t('contract_primary.label')}</Badge> : null
  }
  const choose = async () => {
    setSaving(true)
    try {
      await apiClient.PUT('/api/v1/organizations/{orgCode}/contracts/{contractUid}/assets/{assetUid}/primary', {
        params: { path: { orgCode: org, contractUid, assetUid } },
      })
      await queryClient.invalidateQueries({
        predicate: (q) => {
          const [method, path] = q.queryKey as [string, string]
          return method === 'get' && typeof path === 'string' && path.startsWith('/api/v1/organizations/{orgCode}/contracts')
        },
      })
    } finally {
      setSaving(false)
    }
  }
  return (
    <label className="flex cursor-pointer items-center gap-1 text-xs text-muted-foreground" title={t('contract_primary.title')}>
      <input type="radio" name={`contract-primary-${contractUid}`} checked={isPrimary} disabled={saving}
        onChange={() => void choose()} data-testid={`contract-asset-primary-${assetUid}`} />
      {t('contract_primary.label')}
    </label>
  )
}

/** Shown on a lease of several objects without a primary one. */
export function PrimaryMissingBanner() {
  const { t } = useTranslation('contracts')
  return (
    <Alert className="mb-3" data-testid="contract-asset-primary-missing">
      <AlertDescription>{t('contract_primary.missing')}</AlertDescription>
    </Alert>
  )
}
